// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Form, Checkbox } from 'informed';

import { debounce } from 'throttle-debounce';
import moment from 'moment';

import { Button } from '../Buttons.jsx';
import ContentBox from '../ContentBox.jsx';
import Header from '../Header.jsx';
import PatientCard from '../PatientCard.jsx';
import { SearchMenuForUpdates } from '../searchPage/SearchMenu.jsx';
import SimpleTable from '../SimpleTable.jsx';
import { Label, Error } from '../Typography.jsx';
import NumericText from '../NumericText.jsx';

import { houseComparator } from '../../util/searchUtil';
import type { Update } from '../../util/updatesUtil';
import { findAllUpdates, getUpdateLink, UPDATE_TYPES } from '../../util/updatesUtil';

import styles from '../../styles/SearchPage.scss';

type Props = {
  results: Object,
}

type State = {
  // A map from patient id to an array of his updates. Used to cache results.
  patientToUpdates: Object,

  // The resulting list of all updates matching the search query.
  // updates: Object[],
  updates: Update[],

  // A subset of updates, based on selected filtering options.
  filteredUpdates: Object[],

  // The update lookahead range. Stored in state to prevent unnecessarily
  // recomputing all updates.
  updateRange: number,

  // Whether or not to view the results as a table (as opposed to cards).
  viewAsTable: boolean,

  // The current date.
  date: Date,
}

// The page is rendered at the /updates endpoint. Includes the header, search
// menu, filtering tools, and results.
class UpdatesPageComponent extends Component<Props, State> {
  formApi: {
    getState: Function,
  }

  recomputeFilteredUpdates: Function

  constructor(props: Props) {
    super(props);
    this.state = {
      patientToUpdates: {},
      updates: [],
      filteredUpdates: [],
      updateRange: 30,
      viewAsTable: false,
      date: new Date(),
    };
    this.recomputeFilteredUpdates = this.recomputeFilteredUpdates.bind(this);
  }

  setFormApi = (formApi: Object) => {
    this.formApi = formApi;
  }

  onToggleView = () => {
    this.setState(prevState => ({
      ...prevState,
      viewAsTable: !prevState.viewAsTable,
    }));
  }

  recomputeUpdates() {
    const patients = this.props.results;
    const updates = [];
    const newMapEntries = {};
    patients.forEach((patient) => {
      if (this.state.patientToUpdates[patient._id]) {
        updates.concat(this.state.patientToUpdates[patient._id]);
      } else {
        const newUpdates = [];
        findAllUpdates(newUpdates, patient, this.state.updateRange, true);
        newMapEntries[patient._id] = newUpdates;
        updates.push(...newUpdates);
      }
    });

    // sorts by date then type
    updates.sort((a, b) => {
      if (a.due === b.due) {
        return a.type < b.type ? -1 : 1;
      }
      return a.due < b.due ? -1 : 1;
    });

    this.setState(
      prevState => ({
        ...prevState,
        updates,
        patientToUpdates: {
          ...prevState.patientToUpdates,
          newMapEntries,
        },
      }),
      this.recomputeFilteredUpdates,
    );
  }

  debouncedRecomputeFilteredUpdates = debounce(500, this.recomputeFilteredUpdates).bind(this);

  recomputeFilteredUpdates() {
    const { values } = this.formApi.getState();
    values.updateRange = parseInt(values.updateRange, 10) || 30;
    if (this.state.updateRange !== values.updateRange) {
      this.setState(
        prevState => ({
          ...prevState,
          updateRange: values.updateRange,
        }),
        this.recomputeUpdates,
      );
      return;
    }
    const filtered = this.state.updates.filter((update) => {
      switch (update.type) {
        case UPDATE_TYPES.WCC:
          return values.wccUpdates;
        case UPDATE_TYPES.IMMUNIZATION:
          return values.immunizationUpdates;
        case UPDATE_TYPES.HIV:
          return values.hivUpdates;
        case UPDATE_TYPES.GROWTH:
          return values.growthUpdates;
        case UPDATE_TYPES.HEMOGLOBIN:
          return values.hemoglobinUpdates;
        case UPDATE_TYPES.CUSTOM:
          return values.customUpdates;
        default:
          return true;
      }
    });
    this.setState(prevState => ({
      ...prevState,
      filteredUpdates: filtered,
    }));
  }

  componentDidMount() {
    document.title = 'Work List - HFA EHR';
  }

  componentDidUpdate(prevProps, prevState) {
    // Only recompute updates if the search results changed or the update range
    // changed.
    if (this.props.results !== prevProps.results
      || this.state.updateRange !== prevState.updateRange) {
      this.recomputeUpdates();
    }
    const resultsContainer = document.getElementById('results');
    if (resultsContainer && prevProps.results !== this.props.results) {
      resultsContainer.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }

  resultsAsTable = (updates) => {
    const columns = [
      {
        Header: 'House',
        accessor: 'house',
        sortMethod: houseComparator,
      },
      {
        Header: 'First Name',
        accessor: 'firstName',
      },
      {
        Header: 'Surname',
        accessor: 'surname',
      },
      {
        Header: 'Sex',
        accessor: 'sex',
      },
      {
        Header: 'Age',
        accessor: 'age',
      },
      {
        Header: 'Task Type',
        accessor: 'updateType',
      },
      {
        Header: 'Due',
        Cell: (item) => {
          if (item.original.overdue) {
            return <Error>{item.original.dueText}</Error>;
          }
          return item.original.dueText || '--';
        },
      },
    ];
    const data = updates.map(
      (patient) => {
        const house = patient.houseHistory.length
          ? patient.houseHistory[0].house
          : 'N/A';
        const { dob } = patient.generalInfo;
        const now = moment();
        const years = now.diff(dob, 'years');
        const months = now.diff(dob, 'months') % 12;
        const displayAge = `${years} years, ${months} months`;
        const dueText = `${patient.due.format()}${patient.overdue ? ' (overdue)' : ''}`;
        return {
          house,
          firstName: patient.generalInfo.firstName,
          surname: patient.generalInfo.surname,
          sex: patient.generalInfo.sex,
          age: displayAge,
          updateType: patient.update,
          dueText,
          overdue: patient.overdue,
        };
      },
    );

    return (
      <ContentBox>
        <SimpleTable
          data={data}
          columns={columns}
          style={{}}
        />
      </ContentBox>
    );
  }

  resultsAsCards = (updates) => {
    const resultsByHouse = new Map();
    updates.forEach(
      (patient, index) => {
        const house = patient.houseHistory.length
          ? patient.houseHistory[0].house
          : 'N/A';
        const { dob } = patient.generalInfo;
        const now = moment();
        const years = now.diff(dob, 'years');
        const months = now.diff(dob, 'months') % 12;
        const displayAge = `${years} years, ${months} months`;
        const link = getUpdateLink(patient._id, patient.type);
        const card = (
          <li key={`${patient._id}_${index}`} className={styles.resultCard}>
            <PatientCard
              name={`${patient.generalInfo.firstName} ${patient.generalInfo.surname}`}
              house={house}
              sex={patient.generalInfo.sex}
              age={displayAge}
              extra1={patient.update}
              extra2={`Due: ${patient.due.format()}`}
              overdue={patient.overdue}
              link={link}
            />
          </li>
        );

        const houseList = resultsByHouse.get(house);
        if (houseList) {
          houseList.push(card);
        } else {
          resultsByHouse.set(house, [card]);
        }
      },
    );

    const houses = [...resultsByHouse.keys()].sort(houseComparator);
    const finalList = houses.map(house => (
      <li key={house}>
        <div className={styles.houseHeader}>{house}</div>
        <ul>
          {resultsByHouse.get(house)}
        </ul>
      </li>
    ));

    return (
      <ul>
        {finalList}
      </ul>
    );
  }

  render() {
    const { filteredUpdates } = this.state;
    const results = this.state.viewAsTable
      ? this.resultsAsTable(filteredUpdates)
      : this.resultsAsCards(filteredUpdates);
    return (
      <div className={styles.searchPage}>
        <Header />
        <div className={styles.searchSection}>
          <div className={styles.searchMenu}>
            <div className={styles.title}>Work List</div>
            <Form component={SearchMenuForUpdates} autoComplete='off' />
          </div>
          <div className={styles.updatesPanel}>
            <Form getApi={this.setFormApi} autoComplete='off'>
              <Label>Tasks:</Label>
              <table>
                <tbody>
                  <tr>
                    <td><Checkbox onChange={this.recomputeFilteredUpdates} field='wccUpdates' initialValue={true} /> WCC</td>
                    <td><Checkbox onChange={this.recomputeFilteredUpdates} field='immunizationUpdates' initialValue={true} /> IMM</td>
                    <td><Checkbox onChange={this.recomputeFilteredUpdates} field='hivUpdates' initialValue={true} /> HIV</td>
                  </tr>
                  <tr>
                    <td><Checkbox onChange={this.recomputeFilteredUpdates} field='growthUpdates' initialValue={true} /> Growth</td>
                    <td><Checkbox onChange={this.recomputeFilteredUpdates} field='hemoglobinUpdates' initialValue={true} /> Hemoglobin</td>
                    <td><Checkbox onChange={this.recomputeFilteredUpdates} field='customUpdates' initialValue={true} /> Other</td>
                  </tr>
                </tbody>
              </table>
              <Label>Reminder Range: </Label>
              <NumericText onChange={this.debouncedRecomputeFilteredUpdates} field='updateRange' placeholder={30} size={4} /> days
            </Form>
          </div>
          <div className={styles.toggleButton}>
            <Button onClick={this.onToggleView}>Toggle Card/Table View</Button>
          </div>
          <div id='results'>
            {results}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  results: state.search.updatesData,
});

const UpdatesPage = connect(
  mapStateToProps,
)(UpdatesPageComponent);

export default UpdatesPage;



// WEBPACK FOOTER //
// ./client/src/components/updatesPage/UpdatesPage.jsx